import {FC, useEffect, useState, useMemo} from 'react'
import {CardData, FormField, FormSetting} from '../../../type'
import './PreviewWalletCardContact.scss'
import {useResponsiveCard} from '../useResponsiveCard'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'
import {InfoItem} from './InfoItem'
import PreviewFormSetting from '../PreviewFormSetting/PreviewFormSetting'

interface PreviewWalletCardContactProps {
  cardData: CardData
  showFormSettingModal?: boolean
  promoSettingIds?: string[]
  settingFormFields?: FormSetting[]
  promoActionLabels?: string[]
}

export const PreviewWalletCardContact: FC<PreviewWalletCardContactProps> = ({
  cardData,
  showFormSettingModal = false,
  promoSettingIds,
  settingFormFields,
  promoActionLabels,
}) => {
  const {width, height, fontSize, iconSize, avatarSize} = useResponsiveCard()
  const [isMobile, setIsMobile] = useState(false)
  const {t} = useI18n()

  useEffect(() => {
    const checkMobile = () => {
      const mobileWidth = window.matchMedia('(max-width: 576px)').matches
      setIsMobile(mobileWidth)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  // Memoize card container styles
  const cardContainerStyle = useMemo(
    () => ({
      width: isMobile ? undefined : width,
      fontSize: fontSize,
      height: isMobile ? undefined : height,
    }),
    [isMobile, width, fontSize, height]
  )

  // Memoize background image styles
  const backgroundImageStyle = useMemo(
    () => ({
      height: isMobile ? undefined : `calc(${height} * 0.25)`,
      objectFit: 'cover' as const,
    }),
    [isMobile, height]
  )

  // Memoize avatar styles
  const avatarStyle = useMemo(
    () => ({
      width: isMobile ? undefined : avatarSize,
      height: isMobile ? undefined : avatarSize,
    }),
    [isMobile, avatarSize]
  )

  // Memoize content styles
  const contentStyle = useMemo(
    () => ({
      minHeight: `calc(${height} * 0.5)`,
    }),
    [height]
  )

  // Memoize fullname styles
  const fullnameStyle = useMemo(
    () => ({
      fontSize: `calc(${fontSize} * 1.3)`,
    }),
    [fontSize]
  )

  // Memoize icon styles
  const iconCircleStyle = useMemo(
    () => ({
      minWidth: isMobile ? undefined : `calc(${iconSize} * 0.9)`,
      height: isMobile ? undefined : `calc(${iconSize} * 0.9)`,
      fontSize: isMobile ? undefined : '16px',
      backgroundColor: cardData.contactIconColor,
    }),
    [isMobile, iconSize, cardData.contactIconColor]
  )
  // Memoize first initial for avatar placeholder
  const firstInitial = useMemo(
    () => (cardData.fullName ? cardData.fullName.charAt(0) : ''),
    [cardData.fullName]
  )

  return (
    <div
      className={`d-flex position-relative justify-content-center preview-wallet-card-contact ${
        isMobile ? 'contact-mobile-container' : ''
      }`}
    >
      <div
        className={`digital-card shadow-sm bg-white rounded-4 overflow-hidden ${
          isMobile ? 'digital-card-mobile' : ''
        }`}
        style={cardContainerStyle}
      >
        {/* Image Header */}
        <div
          className='position-relative bg-center bg-no-repeat bg-contain bg-content'
          style={{background: `url(${cardData.backgroundImage})`}}
        >
          {cardData.picture ? (
            <img
              src={cardData.picture}
              alt='Avatar'
              crossOrigin='anonymous'
              className={`avatar-img position-absolute ${
                isMobile ? 'avatar-img-mobile' : 'avatar-img-desktop'
              }`}
              style={avatarStyle}
            />
          ) : (
            <div
              className={`avatar-img position-absolute bg-primary d-flex justify-content-center align-items-center text-white fs-3 fw-bold ${
                isMobile ? 'avatar-img-mobile' : 'avatar-img-desktop'
              }`}
              style={avatarStyle}
            >
              {firstInitial}
            </div>
          )}
          <div
            className={`position-absolute profile-picture-contact ${
              isMobile ? 'profile-picture-contact-mobile' : 'profile-picture-contact-desktop'
            }`}
          >
            <div
              className={`profile-picture-container bg-center bg-no-repeat bg-contain ${
                cardData.logo ? 'bg-logo-company' : ''
              }`}
              style={{background: `url(${cardData.logo || ''})`}}
            ></div>
          </div>
        </div>

        {/* Contact Content */}
        <div
          className={`p-4 contact-content ${isMobile && 'contact-content-mobile'}`}
          style={contentStyle}
        >
          {/* Name Section */}
          <div className={`${isMobile ? 'my-10' : 'my-4'}`}>
            <div className='fw-bold fs-2 contact-fullname text-content contact-fullname-content'>
              {cardData.fullName}
            </div>
            {cardData.jobTitle && (
              <p className='fs-3 m-0 text-content text-dark'>{cardData.jobTitle}</p>
            )}
            <p className='text-muted fs-3 m-0 text-content'>{cardData.companyName}</p>
          </div>

          {/* Info Items */}
          {cardData.email && (
            <InfoItem
              icon='bi-envelope-fill'
              value={cardData.email}
              label={t('WALLET_CARD.FORM.EMAIL')}
              isMobile={isMobile}
              iconCircleStyle={iconCircleStyle}
            />
          )}

          {cardData.phone && (
            <InfoItem
              icon='bi-telephone-fill'
              value={cardData.phone}
              label={t('WALLET_CARD.FORM.PHONE')}
              isMobile={isMobile}
              iconCircleStyle={iconCircleStyle}
            />
          )}

          {cardData.website && (
            <InfoItem
              icon='bi-globe'
              value={cardData.website}
              label={t('WALLET_CARD.FORM.WEBSITE')}
              isMobile={isMobile}
              iconCircleStyle={iconCircleStyle}
            />
          )}

          {cardData.address && (
            <InfoItem
              icon='bi-geo-alt-fill'
              value={cardData.address}
              label={t('WALLET_CARD.FORM.ADDRESS')}
              isMobile={isMobile}
              iconCircleStyle={iconCircleStyle}
            />
          )}
        </div>
      </div>
      {showFormSettingModal && (
        <div className='position-absolute w-100 bottom-0'>
          <PreviewFormSetting
            cardData={cardData}
            showFormSettingModal={showFormSettingModal}
            settingFormFields={settingFormFields}
            promoActionLabels={promoActionLabels}
          />
        </div>
      )}
    </div>
  )
}
